import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { EnumValues } from '@card-software/library';
import { catchError, finalize, of, Subject, takeUntil, tap } from 'rxjs';
import _ from 'lodash';
import { Business } from '../../../models/finance/business';
import { BusinessSearchCommand } from '../../../models/business-search';
import { BusinessEntityType } from '../../../models/business-type-entities';
import { ExternalRequisition } from '../../../models/requisitions/interfaces/external-requisition';
import { ExternalRequestEntry } from '../../../models/requisitions/interfaces/external-request-entry';
import { Quotation, QuotationEntry, QuotationInsert, ProductUI, ValidatedQuotationFormData } from '../../../models/order/quotation';
import { S3Object } from '../../../models/invoice/invoice';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { ExchangeRateProxyService } from '../../../proxies/finance/exchange-rate.proxy.service';
import { QuotationProxyService } from '../../../proxies/order/quotation.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { TableQuotationsComparativeService } from '../../../services/table-quotations-comparative.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { MasterDataActionUtil } from '../../../utilities/mater-data-action-util';
import { ApiResponse, GlobalEntityDefinition, MasterData } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-requisition-quote-details',
  templateUrl: './quotation-details-modal.component.html',
  styleUrl: './quotation-details-modal.component.scss',
})
export class RequisitionQuoteDetailsComponent implements OnInit, OnDestroy, OnChanges {
  @Output() cancelled = new EventEmitter<boolean>();
  private destroy$ = new Subject<void>();

  requisition = input.required<ExternalRequisition>();
  quotation = input<Quotation | null>(null);
  currentQuote = signal<Quotation | null>(null);

  uomOptions = signal<{ label: string; value: number }[]>([]);
  currencyOptions = signal<{ label: string; value: number }[]>([]);
  isEdit = signal<boolean>(false);

  providersToDisplay: Business[] = [];
  providersOptions: Business[] = [];
  isLoading = 0;

  currentFileProduct = signal<File | null>(null);
  currentImagePreview = signal<string | null>(null);

  quoteFormGroup = new FormGroup({
    id: new FormControl<string | null>(null),
    unsavedProviderName: new FormControl<string | null>(null),
    providerReference: new FormControl<string | null>(null),
    paymentMethod: new FormControl<string | null>(null, [Validators.required]),
    currency: new FormControl<number | null>(null, [Validators.required]),
    exchangeRateToUSD: new FormControl<number | null>(null, [Validators.required]),
    providerReferenceNumber: new FormControl<string | null>(null, [Validators.required]),
    issuedDate: new FormControl<Date | null>(null, [Validators.required]),
    expirationDate: new FormControl<Date | null>(null, [Validators.required]),
  });

  itemsFormArray: FormArray<FormGroup> = new FormArray<FormGroup>([]);

  // product form
  productFormGroup = new FormGroup({
    id: new FormControl<string | null>(null),
    itemCode: new FormControl<string | null>(null, Validators.required),
    brand: new FormControl<string | null>(null),
    quantity: new FormControl<number | null>(
      {
        value: null,
        disabled: true,
      },
      [Validators.required],
    ),
    uom: new FormControl<number | null>({
      value: null,
      disabled: true,
    }),
    unitPrice: new FormControl<number | null>(null, { validators: Validators.required, nonNullable: true }),
    isTaxExempt: new FormControl<boolean>(false),
    filesUI: new FormControl<File[] | null>([]),
    filesApi: new FormControl<S3Object[] | null>([]),
  });

  arrayProducts = signal<ProductUI[]>([]);

  // taxes form
  taxesFormGroup = new FormGroup({
    subtotal: new FormControl<number | null>({
      value: 0,
      disabled: true,
    }),
    total: new FormControl<number | null>({
      value: 0,
      disabled: true,
    }),
    ivs: new FormControl<number | null>({
      value: 0,
      disabled: true,
    }),
  });

  public unsavedProvider = new FormControl<boolean>(false, {
    nonNullable: true,
  });

  public selectedProduct = new FormControl<ExternalRequestEntry | null>(null, { validators: [Validators.required] });
  // exchangeRateToUSD: FormControl<number | null> = new FormControl({ value: null, disabled: false });

  private businessProxyService = inject(BusinessProxyService);
  private enumService = inject(EnumService);
  private exchangeRateProxyService = inject(ExchangeRateProxyService);
  private quotationProxyService = inject(QuotationProxyService);
  private tableQuotationsComparativeService = inject(TableQuotationsComparativeService);
  private ctr = inject(ChangeDetectorRef);
  public localeHelperService = inject(LocaleHelperService);

  constructor() {
    const uomEnums = this.transformEnumsValues(this.enumService.getEnum('UnitOfMeasure', 'itemEnums').values);
    this.uomOptions.set(uomEnums);

    const currencyEnums = this.transformEnumsValues(this.enumService.getEnum('Currency', 'financeEnums').values);
    this.currencyOptions.set(currencyEnums);
  }

  ngOnInit(): void {
    const quotation = this.quotation();
    const requisition = this.requisition();

    if (!requisition) return;

    if (quotation) {
      this.isEdit.set(true);
      this.populateFromQuotation(quotation);

      if (quotation.unsavedProviderName && !quotation.providerReference) {
        this.unsavedProvider.setValue(true);
      }
    } else {
      this.isEdit.set(false);
      this.populateFromRequisition();
    }
    this.initializeItemsFormArray();
    this.loadAllProviders();

    this.itemsFormArray.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateTaxesForm();
    });

    this.unsavedProvider.valueChanges.subscribe((unsavedProvider) => {
      if (unsavedProvider) {
        this.quoteFormGroup.get('providerReference')?.setValue(null);
        this.quoteFormGroup.get('providerReference')?.disable();
      } else {
        this.quoteFormGroup.get('providerReference')?.enable();
        this.quoteFormGroup.get('unsavedProviderName')?.setValue(null);
      }
    });

    this.selectedProduct.valueChanges.subscribe((productID) => {
      if (productID) {
        console.log(productID);
        this.populateProductForm(productID as unknown as string);
      }
    });

    // Handle currency change to enable/disable exchange rate
    this.quoteFormGroup
      .get('currency')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((currency) => {
        const exchangeRateControl = this.quoteFormGroup.get('exchangeRateToUSD');
        if (currency === 0) {
          // USD - set to 1 and disable
          exchangeRateControl?.setValue(1);
          exchangeRateControl?.disable();
        } else {
          // HNL or other - enable the control
          exchangeRateControl?.enable();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quotation'] && !changes['quotation'].firstChange) {
      const newQuotation = changes['quotation'].currentValue;

      this.resetValues();

      if (newQuotation) {
        this.isEdit.set(true);
        this.populateFromQuotation(newQuotation);
        this.initializeItemsFormArray();

        if (newQuotation.unsavedProviderName && !newQuotation.providerReference) {
          this.unsavedProvider.setValue(true);
        }
      } else {
        this.isEdit.set(false);
        this.populateFromRequisition();
        this.initializeItemsFormArray();
      }

      if (changes['requisition'] && !changes['requisition'].firstChange) {
        this.initializeItemsFormArray();
      }
    }
  }

  getStrictProductUI(item: unknown): ProductUI {
    return item as ProductUI;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resetValues();
  }

  private loadAllProviders(): void {
    this.isLoading++;
    const searchCommand: BusinessSearchCommand = {
      businessTypes: [BusinessEntityType.Provider],
    };
    this.businessProxyService.search(searchCommand, true, 1, 10000).subscribe({
      next: (response) => {
        this.providersToDisplay = response.data?.items ?? [];
        this.isLoading--;
      },
      error: (e) => {
        console.error(e);
        this.providersToDisplay = [];
        this.isLoading--;
      },
    });
  }

  getItemsDisplayName(itemCode: string): string {
    const requisitionEntry = this.requisition()?.entries.find((entry) => entry.itemCode === itemCode);
    return requisitionEntry?.itemDisplayName?.en ?? 'Name not found';
  }

  get subTotal(): number {
    return this.arrayProducts().reduce((sum, product) => sum + product.unitPrice * product.quantity, 0);
  }

  get taxTotal(): number {
    const TAX_RATE = 0.15;
    return this.arrayProducts().reduce((sum, product) => {
      if (!product.isTaxExempt) {
        return sum + product.unitPrice * product.quantity * TAX_RATE;
      }
      return sum;
    }, 0);
  }

  get grandTotal(): number {
    return this.subTotal + this.taxTotal;
  }

  private populateFromQuotation(q: Quotation) {
    this.quoteFormGroup.patchValue({
      id: q.id ? q.id : undefined,
      unsavedProviderName: q.unsavedProviderName ?? null,
      providerReference: q.providerReference ?? null,
      paymentMethod: q.paymentMethodCode,
      currency: q.currency,
      exchangeRateToUSD: q.exchangeRateToUSD,
      providerReferenceNumber: q.providerReferenceNumber ?? null,
      issuedDate: new Date(q.issuedDate),
      expirationDate: new Date(q.expirationDate),
    });

    // Map quotation items and match with requisition entries to get quantity
    this.arrayProducts.set(
      q.items.map((item: QuotationEntry) => {
        // Find matching requisition entry to get quantity
        const requisitionEntry = this.requisition()?.entries.find((entry) => entry.itemCode === item.itemCode);

        const quantity = requisitionEntry?.quantity ?? 0;
        const uom = requisitionEntry?.uom ?? 0;

        // Calculate total price (with tax if not exempt)
        const basePrice = item.unitPrice * quantity;
        const TAX_RATE = 0.15;
        const totalPrice = item.isTaxExempt ? basePrice : basePrice * (1 + TAX_RATE);

        return {
          id: item.id!,
          itemCode: item.itemCode,
          quantity: quantity,
          uom: uom,
          unitPrice: item.unitPrice,
          isTaxExempt: item.isTaxExempt,
          brand: item.brand ?? '',
          totalPrice: _.round(totalPrice, 2),
          filesUI: undefined,
          filesApi: item.files ?? [],
          uploadStatus: item.files && item.files.length > 0 ? 'success' : undefined,
          retryCount: 0,
          errorMessage: undefined,
          pendingFile: undefined,
        };
      }),
    );
    this.updateTaxesForm();
  }

  private populateFromRequisition() {
    this.quoteFormGroup.patchValue({
      id: null,
      unsavedProviderName: null,
      providerReference: null,
      paymentMethod: null,
      currency: null,
      exchangeRateToUSD: null,
      providerReferenceNumber: null,
      issuedDate: null,
      expirationDate: null,
    });
  }

  handleDelete(event: { item: MasterData; confirmCallback: () => void; cancelCallback: () => void }) {
    MasterDataActionUtil.attemptToDeleteMasterData(event.item).subscribe({
      next: (confirmed) => {
        if (confirmed) {
          event.confirmCallback();
        } else {
          event.cancelCallback();
        }
      },
    });
  }

  showMessagesApi(event: ApiResponse<MasterData>) {
    if (event.success) {
      GlobalMessageUtil.success('Success', `Records has been successfully deleted.`);
    }
  }

  private transformEnumsValues(enums: EnumValues) {
    if (_.isNil(enums)) return [];

    return Object.keys(enums).map((key: string) => {
      return {
        label: enums[Number(key)],
        value: Number(key),
      };
    });
  }

  fetchCurrencyExchangeRateToUSD(): void {
    if (this.quoteFormGroup.get('currency')?.value === 0) {
      this.quoteFormGroup.controls.exchangeRateToUSD.setValue(1, { emitEvent: false });
      return;
    }

    this.isLoading++;
    this.exchangeRateProxyService.getByCurrency(this.quoteFormGroup.get('currency')?.value ?? 0).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.data && response.data.rateToUSD) {
          this.quoteFormGroup.controls.exchangeRateToUSD.setValue(response.data.rateToUSD, { emitEvent: false });
        } else {
          GlobalMessageUtil.warn(
            'Quotation: Warning',
            'No exchange rate found for the selected currency. Please enter the exchange rate manually.',
          );
          this.quoteFormGroup.controls.exchangeRateToUSD.setValue(null, { emitEvent: false });
        }
      },
      error: () => {
        this.quoteFormGroup.controls.exchangeRateToUSD.setValue(null, { emitEvent: false });
        this.isLoading--;
      },
    });
  }

  getUnitOfMeasure(uom: number) {
    return this.uomOptions().find((uomOption) => uomOption.value === uom)?.label ?? '';
  }

  searchProvider(event: AutoCompleteCompleteEvent) {
    const query = event.query;
    const searchCommand: BusinessSearchCommand = {
      name: query,
      code: query,
      businessTypes: [BusinessEntityType.Provider],
    };
    this.businessProxyService.search(searchCommand, true, 1, 25).subscribe({
      next: (response) => {
        this.providersToDisplay = response.data?.items ?? [];
      },
      error: (e) => {
        console.error(e);
        this.providersToDisplay = [];
      },
    });
  }

  onFileClick(s3Object: S3Object) {
    if (s3Object.presignedUrl) {
      window.open(s3Object.presignedUrl, '_blank');
    }
  }

  private populateProductForm(productID: string) {
    if (!_.isNil(this.requisition()?.entries)) {
      const entry = this.requisition()?.entries.find((entry) => entry.lineId === productID);

      if (!_.isNil(entry)) {
        this.productFormGroup.patchValue({
          id: entry.lineId,
          itemCode: entry.itemCode,
          brand: null,
          quantity: entry.quantity,
          uom: entry.uom,
          unitPrice: null,
          isTaxExempt: false,
          filesUI: [],
          filesApi: [],
        });
      }
    }
  }

  private updateTaxesForm(): void {
    this.taxesFormGroup.patchValue(
      {
        subtotal: this.subtotal,
        ivs: this.taxAmount,
        total: this.grandTotal,
      },
      { emitEvent: false },
    );
  }

  saveQuotation() {
    if (_.isNil(this.requisition)) {
      GlobalMessageUtil.warn('Warning', 'Requisition not found');
      return;
    }

    if (!this.quoteFormGroup.valid) {
      GlobalMessageUtil.warn('Warning', 'Please fill in all required fields');
      return;
    }

    // Use getRawValue() to capture disabled form control values
    const rawFormData = this.quoteFormGroup.getRawValue();

    const formData: ValidatedQuotationFormData = {
      departmentReference: this.requisition()!.departmentReference,
      expirationDate: rawFormData.expirationDate?.toISOString() ?? undefined,
      currency: rawFormData.currency ?? undefined,
      exchangeRateToUSD: rawFormData.exchangeRateToUSD ?? undefined,
      paymentMethodCode: rawFormData.paymentMethod ?? undefined,
      issuedDate: rawFormData.issuedDate?.toISOString() ?? undefined,
      initiatorReference: this.requisition().id,
      providerReference: rawFormData.providerReference ?? undefined,
      unsavedProviderName: rawFormData.unsavedProviderName ?? undefined,
      providerReferenceNumber: rawFormData.providerReferenceNumber ?? undefined,
    };

    if (_.isEmpty(_.trim(formData.departmentReference)) || _.isNil(formData.departmentReference)) {
      GlobalMessageUtil.warn('Warning', 'Department Reference is required');
      return;
    }
    if (_.isEmpty(_.trim(formData.expirationDate)) || _.isNil(formData.expirationDate)) {
      GlobalMessageUtil.warn('Warning', 'Expiration Date is required');
      return;
    }
    if (_.isNil(formData.currency)) {
      GlobalMessageUtil.warn('Warning', 'Currency is required');
      return;
    }
    if (_.isNil(formData.exchangeRateToUSD) || !_.isNumber(formData.exchangeRateToUSD) || formData.exchangeRateToUSD <= 0) {
      GlobalMessageUtil.warn('Warning', 'Exchange Rate to USD is required');
      return;
    }
    if (_.isEmpty(_.trim(formData.paymentMethodCode)) || _.isNil(formData.paymentMethodCode)) {
      GlobalMessageUtil.warn('Warning', 'Payment Method is required');
      return;
    }
    if (_.isEmpty(_.trim(formData.issuedDate)) || _.isNil(formData.issuedDate)) {
      GlobalMessageUtil.warn('Warning', 'Issued Date is required');
      return;
    }
    if (_.isEmpty(_.trim(formData.initiatorReference)) || _.isNil(formData.initiatorReference)) {
      GlobalMessageUtil.warn('Warning', 'Requisition ID is required');
      return;
    }
    if (_.isEmpty(_.trim(formData.providerReference)) && _.isEmpty(_.trim(formData.unsavedProviderName))) {
      GlobalMessageUtil.warn('Warning', 'Either Provider or Unsaved Provider Name must be specified');
      return;
    }

    const quotation: QuotationInsert = {
      departmentReference: formData.departmentReference,
      expirationDate: formData.expirationDate,
      currency: formData.currency!,
      exchangeRateToUSD: formData.exchangeRateToUSD!,
      paymentMethodCode: formData.paymentMethodCode,
      issuedDate: formData.issuedDate,
      initiator: {
        reference: formData.initiatorReference,
        entityDefinitionCode: GlobalEntityDefinition.ExternalRequisition,
      },
      // items: this.currentQuote()?.items || [],
      items: this.getFilteredItemsFromFormArray(),
      providerReference: formData.providerReference,
      unsavedProviderName: formData.unsavedProviderName,
      providerReferenceNumber: formData.providerReferenceNumber,
      number: this.requisition()!.number,
    };

    console.log(this.getFilteredItemsFromFormArray());

    if (this.isEdit() && this.quotation()?.id) {
      quotation.id = this.quotation()!.id;
    } else if (this.currentQuote()?.id) {
      quotation.id = this.currentQuote()!.id;
    }

    this.isLoading++;
    const apiCall = quotation.id
      ? this.quotationProxyService.updateQuotation(quotation)
      : this.quotationProxyService.insertQuotation(quotation);

    apiCall.subscribe({
      next: (response) => {
        this.isLoading--;
        const data = 'data' in response ? response.data : response;

        if (!_.isNil(data)) {
          this.currentQuote.set(data);
          this.isEdit.set(true);
          this.initializeItemsFormArray();
          GlobalMessageUtil.success('Success', 'Quotation saved! You can now add items with files.');
        }
      },
      error: (error) => {
        this.isLoading--;
        console.error('Error saving quotation', error);
      },
    });
  }

  get subtotal(): number {
    const products = this.itemsFormArray.controls.map((control) => control.getRawValue());
    const subtotal = products.reduce((sum, product) => {
      const unitPrice = product.unitPrice ?? 0;
      const quantity = product.quantity ?? 0;
      return sum + unitPrice * quantity;
    }, 0);

    return _.round(subtotal, 2);
  }

  get taxAmount(): number {
    const TAX_RATE = 0.15;
    const products = this.itemsFormArray.controls.map((control) => control.getRawValue());

    const tax = products.reduce((sum, product) => {
      const unitPrice = product.unitPrice ?? 0;
      const quantity = product.quantity ?? 0;
      const isTaxExempt = product.isTaxExempt ?? false;

      if (!isTaxExempt && unitPrice > 0 && quantity > 0) {
        return sum + unitPrice * quantity * TAX_RATE;
      }
      return sum;
    }, 0);

    return _.round(tax, 2);
  }

  get total(): number {
    return _.round(this.subtotal + this.taxAmount, 2);
  }

  closeQuote() {
    this.cancelled.emit(false);
    this.resetValues();
  }

  resetValues() {
    this.currentQuote.set(null);
    this.arrayProducts.set([]);
    this.quoteFormGroup.reset();
    this.productFormGroup.reset();
    this.selectedProduct.setValue(null);
    this.quoteFormGroup.markAsUntouched();
    this.productFormGroup.markAsUntouched();
    this.selectedProduct.markAsUntouched();
    this.quoteFormGroup.markAsPristine();
    this.productFormGroup.markAsPristine();
    this.selectedProduct.markAsPristine();
    this.unsavedProvider.setValue(false);
    // while (this.itemsFormArray.length) {
    //   this.itemsFormArray.removeAt(0);
    // }
  }

  openImageInNewTab(presignedUrl: string): void {
    window.open(presignedUrl, '_blank');
  }

  getSelectedCurrency(): string {
    const currency = this.quoteFormGroup.get('currency')?.value;

    switch (currency) {
      case 0:
        return 'USD';
      case 1:
        return 'HNL';
      case 2:
        return 'EUR';
      default:
        return 'USD';
    }
  }

  get isLocalCurrency(): boolean {
    const currency = this.quoteFormGroup.get('currency')?.value;
    return currency !== 0 && currency !== null;
  }

  /**
   * Returns the price as-is since prices are stored in the selected currency.
   * This method is kept for backward compatibility but prices don't need conversion for display.
   */
  convertPriceToSelectedCurrency(priceInSelectedCurrency: number): number {
    // Prices are stored in the selected currency, so no conversion needed for display
    return priceInSelectedCurrency;
  }

  /**
   * Converts a price from local currency to USD.
   * Use this to display the USD equivalent when the currency is not USD.
   */
  getValueOnUSD(value: number): number {
    const exchangeRate = this.quoteFormGroup.get('exchangeRateToUSD')?.value;
    if (exchangeRate === null || exchangeRate === undefined || exchangeRate <= 0) {
      return value;
    }
    return parseFloat((value / exchangeRate).toFixed(5));
  }

  calculateItemTotalPriceFromForm(itemFormGroup: FormGroup): number {
    const unitPrice = itemFormGroup.get('unitPrice')?.value ?? 0;
    const quantity = itemFormGroup.get('quantity')?.value ?? 0;
    const isTaxExempt = itemFormGroup.get('isTaxExempt')?.value ?? false;

    const basePrice = unitPrice * quantity;
    const TAX_RATE = 0.15;
    const totalPrice = isTaxExempt ? basePrice : basePrice * (1 + TAX_RATE);

    return _.round(totalPrice, 2);
  }

  private getFilteredItemsFromFormArray(): QuotationEntry[] {
    const items: QuotationEntry[] = [];

    this.itemsFormArray.controls.forEach((control) => {
      const formGroup = control as FormGroup;
      const unitPrice = formGroup.get('unitPrice')?.value;
      const itemCode = formGroup.get('itemCode')?.value;

      if (unitPrice !== null && unitPrice !== undefined && unitPrice > 0) {
        const existingId = formGroup.get('id')?.value;

        items.push({
          id: existingId || undefined,
          itemCode: itemCode,
          brand: formGroup.get('brand')?.value || '',
          unitPrice: unitPrice,
          isTaxExempt: formGroup.get('isTaxExempt')?.value ?? false,
          files: formGroup.get('filesApi')?.value ?? [],
        });
      }
    });

    return items;
  }

  private createItemFormGroup(entry: ExternalRequestEntry, existingItem?: QuotationEntry): FormGroup {
    return new FormGroup({
      lineId: new FormControl<string>(entry.lineId!),
      itemCode: new FormControl<string>(entry.itemCode),
      itemDisplayName: new FormControl<unknown>(entry.itemDisplayName),
      quantity: new FormControl<number>({ value: entry.quantity, disabled: true }),
      uom: new FormControl<number>({ value: entry.uom, disabled: true }),
      brand: new FormControl<string | null>(existingItem?.brand ?? null),
      unitPrice: new FormControl<number | null>(existingItem?.unitPrice ?? null),
      isTaxExempt: new FormControl<boolean>(existingItem?.isTaxExempt ?? false),
      filesUI: new FormControl<File[]>([]),
      filesApi: new FormControl<S3Object[]>(existingItem?.files ?? []),
      id: new FormControl<string | null>(existingItem?.id ?? null),
    });
  }

  private initializeItemsFormArray(): void {
    const requisition = this.requisition();
    const quotation = this.currentQuote() || this.quotation();

    while (this.itemsFormArray.length) {
      this.itemsFormArray.removeAt(0);
    }

    requisition.entries.forEach((entry) => {
      const existingItem = quotation?.items.find((item) => item.itemCode === entry.itemCode);
      const itemFormGroup = this.createItemFormGroup(entry, existingItem);
      this.itemsFormArray.push(itemFormGroup);
    });
  }

  openFileUpload(rowIndex: number): void {
    const formGroup = this.itemsFormArray.at(rowIndex) as FormGroup;
    const itemId = formGroup.get('id')?.value;
    const quotationId = this.currentQuote()?.id ?? this.quotation()?.id;

    // Validate before opening file dialog
    if (!quotationId) {
      GlobalMessageUtil.error('Error', 'Please save the quotation first');
      return;
    }

    if (!itemId) {
      GlobalMessageUtil.error('Error', 'Please save this item first by clicking Save button');
      return;
    }

    // All validations passed, open file dialog
    const fileInput = document.getElementById(`fileInput-${rowIndex}`) as HTMLInputElement;
    fileInput?.click();
  }

  handleFilesInput(event: Event, rowIndex: number): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type.toLowerCase())) {
      GlobalMessageUtil.warn('Invalid File Type', 'Only JPEG and PNG images are allowed');
      target.value = '';
      return;
    }

    const formGroup = this.itemsFormArray.at(rowIndex) as FormGroup;
    const itemId = formGroup.get('id')?.value;
    const quotationId = this.currentQuote()?.id ?? this.quotation()?.id;

    if (!quotationId || !itemId) {
      GlobalMessageUtil.error('Error', 'Cannot upload file. Please save the quotation first.');
      target.value = '';
      return;
    }

    this.uploadFileForItem(quotationId, itemId, file);

    target.value = '';
  }

  private uploadFileForItem(quotationId: string, itemId: string, file: File): void {
    this.isLoading++;

    this.quotationProxyService
      .uploadItemFile(quotationId, itemId, file)
      .pipe(
        tap(() => {
          GlobalMessageUtil.success('Success', 'File uploaded successfully!');

          this.refreshQuotationData(quotationId);
        }),
        catchError((error) => {
          console.error('Failed to upload file:', error);
          return of(null);
        }),
        finalize(() => {
          this.isLoading--;
        }),
      )
      .subscribe();
  }

  private refreshQuotationData(quotationId: string): void {
    this.isLoading++;
    this.quotationProxyService.getById(quotationId).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.data) {
          this.currentQuote.set(response.data);
          this.initializeItemsFormArray();
        }
      },
      error: (error) => {
        this.isLoading--;
        console.error('Failed to refresh quotation data:', error);
      },
    });
  }
}
